import React from 'react';
import { useHistory } from 'react-router-dom';
import {
    Dropdown,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    Nav,
    NavItem,
    NavLink
} from 'reactstrap';
import styled from 'styled-components';
import { AppContext } from '../../App';
import {
    CAC_TYPE,
    CROP_REPORT,
    CUSTOMER_VISITS,
    NURSERY_REPORT,
    RESOURCES,
    USER_ROLES
} from '../../res/Constants';
import {
    getCurrentRole,
    getCurrentUserDataAsync,
    isCurrentUserAdmin,
    logout
} from '../../service/AccountService';
import { getUnreadExtraReportsCount } from '../../service/ExtraReportService';
import { canSeeCrAlerts } from '../../service/PermissionService';
import { ButtonNoStyle } from './../Common';
import DownloadApplicationIcon from './components/DownloadApplicationIcon';
import HamburgerMenu from './components/HamburgerMenu';
import UserDropDown from './components/UserDropDown';
import './header.scss';

const CAC_SECTIONS = [
    { label: CROP_REPORT, type: CAC_TYPE.CROP },
    { label: NURSERY_REPORT, type: CAC_TYPE.NURSERY },
    { label: CUSTOMER_VISITS, type: CAC_TYPE.VISITS, clientVisible: false }
];

export default function NavigationBar() {
    const history = useHistory();
    const appContext = React.useContext(AppContext);

    const [username, setUsername] = React.useState(null);
    const [unreadCount, setUnreadCount] = React.useState(0);
    const [dropdownOpen, setDropdownOpen] = React.useState(false);

    React.useEffect(() => {
        let active = true;
        getCurrentUserDataAsync().then((userData) => {
            if (!active || userData == null) return;
            setUsername(userData.username);
            if (userData.role !== USER_ROLES.client) {
                getUnreadExtraReportsCount((count) => {
                    if (active) setUnreadCount(count);
                });
            }
        });
        return () => {
            active = false;
        };
    }, []);

    const currentRole = getCurrentRole();
    const isResourcesActive = appContext.cacType === CAC_TYPE.RESOURCES;
    const canSeeResources = currentRole !== USER_ROLES.client;
    const cacSectionsFilteredByRole = CAC_SECTIONS.filter(
        (s) => s.clientVisible !== false || currentRole !== USER_ROLES.client
    );

    const onSectionChange = (cacType) => {
        appContext.onCacTypeChange(cacType);
        const target = cacType === CAC_TYPE.VISITS ? '/visits' : '/techforms';
        history.push(target);
    };

    const handleLogout = () => {
        logout((result) => {
            history.push('/');
            appContext.clearCache();
            if (result.status !== 200) {
                console.log('logout failed');
            }
        });
    };

    const onAlertBadgeClick = () => {
        appContext.onCacTypeChange(CAC_TYPE.ALERTS);
        history.push('/reports/extra');
    };

    const onSettingsBadgeClick = () => {
        appContext.onCacTypeChange(CAC_TYPE.SETTINGS);
        history.push('/controlpanel');
    };

    const onResourcesClick = () => {
        appContext.onCacTypeChange(CAC_TYPE.RESOURCES);
        history.push('/resources');
    };

    const toggleDropDown = () => {
        setDropdownOpen((curr) => !curr);
    };

    return (
        <div id='header-container'>
            <div style={{ margin: '0 10px' }}>
                <img
                    id='logo'
                    className='responsive-margin'
                    src={require('../../res/images/cac_logo_negative.png')}
                    alt='CAC logo'
                />
            </div>
            <div className='nav-menu'>
                <Nav pills className='d-md-none'>
                    <Dropdown nav isOpen={dropdownOpen} toggle={toggleDropDown}>
                        <DropdownToggle nav caret>
                            {isResourcesActive
                                ? RESOURCES
                                : (cacSectionsFilteredByRole.find(
                                      (section) => section.type === appContext.cacType
                                  )?.label ?? CROP_REPORT)}
                        </DropdownToggle>
                        <DropdownMenu>
                            {cacSectionsFilteredByRole
                                .filter((section) => section.type !== appContext.cacType)
                                .map((section) => (
                                    <DropdownItem
                                        key={section.type}
                                        onClick={() => onSectionChange(section.type)}>
                                        {section.label}
                                    </DropdownItem>
                                ))}
                            {canSeeResources && !isResourcesActive && (
                                <DropdownItem onClick={onResourcesClick}>
                                    {RESOURCES}
                                </DropdownItem>
                            )}
                        </DropdownMenu>
                    </Dropdown>
                </Nav>
                <Nav pills className='d-none d-md-flex'>
                    {cacSectionsFilteredByRole.map((section) => (
                        <NavItem key={section.type}>
                            <NavLink
                                href='#'
                                active={appContext.cacType === section.type}
                                onClick={() => onSectionChange(section.type)}>
                                {section.label}
                            </NavLink>
                        </NavItem>
                    ))}
                    {canSeeResources && (
                        <NavItem>
                            <NavLink
                                href='#'
                                active={isResourcesActive}
                                onClick={onResourcesClick}>
                                {RESOURCES}
                            </NavLink>
                        </NavItem>
                    )}
                </Nav>
            </div>
            <div className='d-right-buttons d-lateral-icons responsive-margin'>
                {isCurrentUserAdmin() && (
                    <img
                        className='header-icon d-none d-lg-block'
                        src={require('../../res/images/cac_settings.png')}
                        alt='Settings'
                        onClick={onSettingsBadgeClick}
                        style={{ cursor: 'pointer' }}
                    />
                )}
                <DownloadApplicationIcon className='d-none d-lg-block' />
                <UserDropDown
                    className='d-none d-lg-block'
                    handleLogout={handleLogout}
                    username={username}
                />
                {canSeeCrAlerts() && (
                    <StyledNavButton
                        onClick={onAlertBadgeClick}
                        $isActive={appContext.cacType === CAC_TYPE.ALERTS}>
                        {unreadCount > 0 && (
                            <StyledBadge>
                                <p>{unreadCount}</p>
                            </StyledBadge>
                        )}
                        <img
                            className='header-icon'
                            src={require('../../res/images/cac_alert.png')}
                            alt='Alert icon'
                        />
                    </StyledNavButton>
                )}
                <HamburgerMenu
                    className='d-lg-none'
                    username={username}
                    onLogout={handleLogout}
                    onSettings={onSettingsBadgeClick}
                />
            </div>
        </div>
    );
}

const StyledNavButton = styled(ButtonNoStyle)`
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 30px !important;
    width: 30px !important;
    background-color: ${(props) => (props.$isActive ? 'white' : 'transparent')};
    border-radius: 50%;
    transition: all 0.3s ease;
    cursor: pointer;

    &:hover {
        opacity: 0.8;
    }
`;

const StyledBadge = styled.div`
    position: absolute;
    margin-left: 8px;
    margin-top: -2px;
    border-radius: 10px;
    background-color: red;

    p {
        font-size: 10px;
        margin: 0;
        color: white;
        padding: 1px 2px;
    }
`;
